import React, { useCallback } from 'react';
import { Field, useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import WithLabel, { LABEL_STYLES } from '../../../../components/with-label/WithLabel';
import WithError from '../../../../components/with-error/WithError';
import CommonDropdown from '../../../../components/common-dropdown/CommonDropdown';
import useResonanceThemeOptions from '../../../../hooks/useResonanceThemeOptions';
import useResonanceVisionOptions from '../../../../hooks/useResonanceVisionOptions';
import useResonanceStructureOptions from '../../../../hooks/useResonanceStructureOptions';
import { RESONANCE_STRUCTURE, RESONANCE_VISIONS } from '../../../../constants';
import useResonanceLanguageOptions from '../../../../hooks/useResonanceLanguageOptions';
import DropdownWithIcons from '../../../../components/common-dropdown/DropdownWithIcons';

function ResonanceNameFormInner() {
  const { t } = useTranslation('projects');

  const resonanceThemeOptions = useResonanceThemeOptions();
  const resonanceVisionOptions = useResonanceVisionOptions();
  const resonanceStructureOptions = useResonanceStructureOptions();
  const resonanceLanguageOptions = useResonanceLanguageOptions();

  const editProject = useSelector((state) => state.projects.editProject);
  const isNameSectionLoading = useSelector((state) => state.projects.isNameSectionLoading);

  const {
    input: { value: dataStructureValue, onChange: changeDataStructure },
  } = useField('dataStructure');

  const {
    input: { onChange: changeVision },
  } = useField('vision');

  const handleDataStructureChange = useCallback(
    (newValue) => {
      changeDataStructure(newValue);

      if (newValue === RESONANCE_STRUCTURE.RESPONDENTS) {
        changeVision(RESONANCE_VISIONS.DEFAULT);
      }
    },
    [changeDataStructure, changeVision],
  );

  return (
    <>
      <Field name='theme'>
        {({ input, meta }) => (
          <WithLabel variant={LABEL_STYLES.ALIGNED} label={t('theme')} maxWidth={360} isBold>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <CommonDropdown
                value={input.value}
                options={resonanceThemeOptions}
                onSelect={input.onChange}
                name={input.name}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                isError={meta.touched && meta.error}
                disabled={!editProject || isNameSectionLoading}
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='dataStructure'>
        {({ input, meta }) => (
          <WithLabel variant={LABEL_STYLES.ALIGNED} label={t('dataStructure')} maxWidth={360} isBold>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <CommonDropdown
                value={input.value}
                options={resonanceStructureOptions}
                onSelect={handleDataStructureChange}
                name={input.name}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                isError={meta.touched && meta.error}
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      {dataStructureValue === RESONANCE_STRUCTURE.RESPONDENTS ? (
        <Field name='lang'>
          {({ input, meta }) => (
            <WithLabel variant={LABEL_STYLES.ALIGNED} label={t('language')} maxWidth={360} isBold>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <DropdownWithIcons
                  value={input.value}
                  options={resonanceLanguageOptions}
                  onSelect={input.onChange}
                  name={input.name}
                  onBlur={input.onBlur}
                  onFocus={input.onFocus}
                  isError={meta.touched && meta.error}
                  isEmojiIcons
                />
              </WithError>
            </WithLabel>
          )}
        </Field>
      ) : null}

      {dataStructureValue === RESONANCE_STRUCTURE.MESSAGES ? (
        <Field name='vision'>
          {({ input, meta }) => (
            <WithLabel variant={LABEL_STYLES.ALIGNED} label={t('vision')} maxWidth={360} isBold>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <CommonDropdown
                  value={input.value}
                  options={resonanceVisionOptions}
                  onSelect={input.onChange}
                  name={input.name}
                  onBlur={input.onBlur}
                  onFocus={input.onFocus}
                  isError={meta.touched && meta.error}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>
      ) : null}
    </>
  );
}

export default ResonanceNameFormInner;
